// Light / dark theme, stored on the ACCOUNT (2026-08-15).
//
// Two consumers share this hook — the avatar menu's toggle and the
// Settings > Appearance radio group — so they can't drift apart.
//
// The tricky part is that the theme has to be painted BEFORE React
// mounts. index.html runs a tiny pre-hydration script that reads the
// `stewardshipos.darkMode` localStorage key and adds `html.dark`; without
// it, every load would flash light and then repaint. An account-stored
// preference can't answer that — the fetch hasn't happened yet.
//
// So the local key survives as a PAINT CACHE, which is the carve-out the
// CLAUDE.md storage rule allows ("localStorage as an instant-render cache
// only"). The account row is the preference. The sequence is:
//
//   1. Pre-hydration script paints from the cache. No flash.
//   2. Account settings load. If the account disagrees with what was
//      painted — a user who turned dark on at home and is now on a fresh
//      machine — we repaint and refresh the cache, so the NEXT load on
//      this machine is right from the first frame.
//   3. A toggle writes both: the account (the preference) and the cache
//      (so the pre-hydration script agrees next time).
//
// Step 2 is the one visible compromise in this program: on a brand-new
// browser, a dark-mode user gets one light frame before the theme
// settles. Fixing that would mean blocking first paint on a network
// round-trip, which is a worse trade for every load thereafter.

import { useEffect } from "react";

import {
  applyDarkModeClass,
  readInitialDarkMode,
  writeDarkModePreference,
} from "../components/user-menu-helpers";
import { useUserSetting, useUserSettingsLoaded } from "./user-settings-context";
import { APPEARANCE_DARK_MODE } from "./user-settings-registry";

export interface UseDarkModeResult {
  isDark: boolean;
  setDark: (next: boolean) => void;
  toggle: () => void;
}

export function useDarkMode(): UseDarkModeResult {
  const [accountDark, setAccountDark] = useUserSetting(APPEARANCE_DARK_MODE);
  const isLoaded = useUserSettingsLoaded();

  // Until the account answers, the truth is whatever the pre-hydration
  // script painted — read back from the same cache it used. Reporting the
  // baseline here instead would make the avatar menu's switch flick off
  // and back on during every load for a dark-mode user.
  const isDark = isLoaded ? accountDark : readInitialDarkMode();

  useEffect(() => {
    // Critical: do NOT touch the class before the account has loaded.
    // The pre-hydration script has already painted the right theme, and
    // applying the not-yet-loaded default would strip `html.dark` and
    // flash light on every load — then repaint dark a moment later.
    if (!isLoaded) return;
    applyDarkModeClass(accountDark);
    // Refresh the paint cache so the NEXT load on this machine is right
    // from the first frame — including the case where the account and
    // this browser disagreed when we started.
    writeDarkModePreference(accountDark);
  }, [isLoaded, accountDark]);

  return {
    isDark,
    setDark: setAccountDark,
    toggle: () => setAccountDark(!isDark),
  };
}
